import {
  FluentProvider,
  Text,
  TabList,
  Tab,
  Dropdown,
  Option,
  Button,
  makeStyles,
  tokens,
  shorthands,
} from '@fluentui/react-components';
import { appTheme } from '../common/appTheme';
import {
  Board20Regular,
  BuildingMultiple20Regular,
  DocumentText20Regular,
  Tag20Regular,
  Shield20Regular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import { setTenantId, goDashboard, goTenant } from '../slices/navSlice';
import {selectTenantOptions, selectTenantNameById, selectTenantsStatus, selectTenantsError } from '../slices/tenantsSlice';

import TenantKPICards from './TenantKPICards';
import TenantSITTable from './TenantSITTable';
import TenantLabelsTable from './TenantLabelsTable';
import TenantPoliciesTable from './policies/TenantPoliciesTable';
import { LabelTabName, PolicyTabName, TenantEntityType, BASE_TEMPLATE_TENANT, TENANT_HEADING } from '../common/constants';
import { TenantDashboardStyles } from './Styles';

type TenantOption = { id: string; name: string };
const makeDisplay = (name?: string, id?: string) =>
  id ? `${name || 'Tenant'} — ${id}` : name || '';

export default function TenantDashboard() {
  const styles = TenantDashboardStyles();
  const dispatch = useDispatch<AppDispatch>();

  const [activeTab, setActiveTab] = useState<'sit' | 'labels' | 'policies'>('labels');

  const { view } = useSelector((s: RootState) => s.nav);
  const isTenant = view === 'tenant';
  const isDashboard = view === 'dashboard';

  const selectedTenantId = useSelector((s: RootState) => s.nav.tenantId) || '';
  const tenantOptions = useSelector(selectTenantOptions) as TenantOption[];
  const tenantsStatus = useSelector(selectTenantsStatus);
  const tenantsError = useSelector(selectTenantsError);
  const selectedTenantName = useSelector((s: RootState) =>
    selectTenantNameById(s, selectedTenantId),
  );

  // Seed tenantId if missing
  useEffect(() => {
    if (!selectedTenantId && tenantOptions.length > 0) {
      dispatch(setTenantId(tenantOptions[0].id));
    }
  }, [selectedTenantId, tenantOptions]);

  // Fix accessibility issues with Fluent UI tabster dummy elements
  useEffect(() => {
    const fixTabsterElements = () => {
      const tabsterElements = document.querySelectorAll('[data-tabster-dummy][aria-hidden="true"]');
      tabsterElements.forEach((element) => {
        if (element.getAttribute('tabindex') === '0') {
          element.setAttribute('tabindex', '-1');
        }
      });
    };

    // Fix immediately and on mutations
    fixTabsterElements();

    const observer = new MutationObserver(fixTabsterElements);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['tabindex', 'aria-hidden'],
    });

    return () => observer.disconnect();
  }, []);

  const selectedTenantDisplay = makeDisplay(
    selectedTenantName || undefined,
    selectedTenantId || undefined,
  );

  const optionsWithFallback = useMemo(() => {
    const exists = tenantOptions.some((o) => o.id === selectedTenantId);
    return exists || !selectedTenantId
      ? tenantOptions
      : [{ id: selectedTenantId, name: selectedTenantName || selectedTenantId }, ...tenantOptions];
  }, [tenantOptions, selectedTenantId, selectedTenantName]);

  return (
    <FluentProvider theme={appTheme}>
      <div className={styles.pageWrap}>
        <div className={styles.surface}>
          {/* Heading */}
          <div className={styles.headingWrap}>
            <div className={styles.headingRow}>
              <Text as="h1" className={styles.headingTitle}>
                {TENANT_HEADING}
              </Text>

              <div className={styles.rightControls}>
                <Text className={styles.baseTemplateText}>
                  <span className="base-template-label">Base template:</span>
                  <span>{BASE_TEMPLATE_TENANT}</span>
                </Text>

                <Dropdown
                  key={`tenant-dropdown-${selectedTenantId}-${tenantOptions.length}`}
                  aria-label="Select tenant"
                  className={styles.tenantPicker}
                  placeholder="Select tenant…"
                  value={selectedTenantDisplay || ''}
                  selectedOptions={selectedTenantId ? [selectedTenantId] : []}
                  onOptionSelect={(_, data) => {
                    const id = (data.optionValue || '') as string;
                    if (id) {
                      dispatch(setTenantId(id));
                      dispatch(goTenant(id));
                    }
                  }}
                >
                  {optionsWithFallback.map((t) => (
                    <Option key={t.id} value={t.id} text={`${t.name} — ${t.id}`}>
                      {t.name} — {t.id}
                    </Option>
                  ))}
                </Dropdown>

                <nav aria-label="Primary view switcher" className={styles.switchWrap}>
                  <Button
                    appearance={isTenant ? 'primary' : 'secondary'}
                    size="large"
                    icon={<BuildingMultiple20Regular />}
                    aria-pressed={isTenant}
                    aria-current={isTenant ? 'page' : undefined}
                    onClick={() =>
                      dispatch(goTenant(selectedTenantId || (tenantOptions[0]?.id ?? '')))
                    }
                  >
                    Tenant View
                  </Button>
                  <Button
                    appearance={isDashboard ? 'primary' : 'secondary'}
                    size="large"
                    icon={<Board20Regular />}
                    aria-pressed={isDashboard}
                    aria-current={isDashboard ? 'page' : undefined}
                    onClick={() => dispatch(goDashboard())}
                  >
                    Dashboard
                  </Button>
                </nav>
              </div>
            </div>

            <Text as="span" className={styles.headingSub}>
              Manage SITs, sensitivity labels, and DLP policies for the selected tenant
            </Text>
          </div>

          {/* KPI cards */}
          {selectedTenantId && <TenantKPICards tenantId={selectedTenantId} />}

          {/* Tabs + Export */}
          <div className={styles.tabsAndToolbarRow}>
            <TabList
              aria-label="Tenant configuration sections"
              selectedValue={activeTab}
              onTabSelect={(_, d) => setActiveTab(d.value as 'sit' | 'labels' | 'policies')}
              className={styles.accessibleTabList}
            >
              {/* SIT tab hidden — deployment not yet supported */}
              {false && <Tab value="sit">
                <div className={styles.tabWithIcon}>
                  <DocumentText20Regular />
                  <span className={styles.highContrastText}>SITs</span>
                </div>
              </Tab>}
              <Tab value="labels">
                <div className={styles.tabWithIcon}>
                  <Tag20Regular />
                  <span className={styles.highContrastText}>Sensitivity Labels</span>
                </div>
              </Tab>
              <Tab value="policies">
                <div className={styles.tabWithIcon}>
                  <Shield20Regular />
                  <span className={styles.highContrastText}>Policies</span>
                </div>
              </Tab>
            </TabList>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            </div>
          </div>

          {/* Content */}
          <section className={styles.section}>
            {false && activeTab === TenantEntityType && (
              <TenantSITTable tenantId={selectedTenantId} tenantDisplay={selectedTenantDisplay} />
            )}
            {activeTab === LabelTabName && (
              <TenantLabelsTable
                tenantId={selectedTenantId}
                tenantDisplay={selectedTenantDisplay}
              />
            )}
            {activeTab === PolicyTabName && (
              <TenantPoliciesTable
                tenantId={selectedTenantId}
                tenantDisplay={selectedTenantDisplay}
              />
            )}
          </section>
        </div>
      </div>
    </FluentProvider>
  );
}
